// frontend/src/components/ProjectDetail/panels/ExpensesPanel.js
import React, { useMemo, useState } from 'react';
import { Edit3, ExternalLink, FileText, Search, User, X } from 'lucide-react';
import { getIterationTint } from '../../../utils/iterationTints';

const FALLBACK_CURRENCY = '\u20ac';

const formatCurrency = (value, currency = FALLBACK_CURRENCY) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }
  const resolvedCurrency = currency && currency.trim() !== '' ? currency : FALLBACK_CURRENCY;
  return `${numericValue.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${resolvedCurrency}`;
};

const formatPercentage = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }
  const rounded = Math.round(numericValue * 100) / 100;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
};

const formatExpenseDate = (date) => {
  if (!date) {
    return 'N/A';
  }
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'N/A';
  }
  return parsedDate.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const normalizeSearch = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const toValidNumberOrNull = (value) => {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getExpenseFilename = (expense) => {
  if (!expense?.file_path) {
    return null;
  }
  return expense.file_path.split('/').pop();
};

const getExpenseFileUrl = (apiBaseUrl, filename) =>
  `${apiBaseUrl}/expenses/files/${encodeURIComponent(filename)}`;

const ExpensesPanel = ({ projectId, expenses, iterations = [], apiBaseUrl, selectedIterationId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [previewExpense, setPreviewExpense] = useState(null);

  const processedExpenses = useMemo(() => {
    const projectIdNumeric = toValidNumberOrNull(projectId);
    const iterationIdNumeric = toValidNumberOrNull(selectedIterationId);
    const iterationMetaById = new Map(
      (Array.isArray(iterations) ? iterations : []).map((iteration, index) => [
        toValidNumberOrNull(iteration?.id),
        {
          id: toValidNumberOrNull(iteration?.id),
          name: iteration?.name || `Itération ${iteration?.id}`,
          tint: getIterationTint(index),
        },
      ])
    );

    if (!Array.isArray(expenses)) {
      return [];
    }

    return expenses.map((expense) => {
      const amount = expense?.amount != null ? Number(expense.amount) : null;
      const currency = expense?.currency && expense.currency.trim() !== '' ? expense.currency : FALLBACK_CURRENCY;
      const allocations = Array.isArray(expense?.project_allocations) ? expense.project_allocations : [];
      const hasAllocations = allocations.length > 0;

      const matchingProjectAllocations =
        projectIdNumeric != null
          ? allocations.filter((alloc) => toValidNumberOrNull(alloc?.project_id) === projectIdNumeric)
          : allocations;

      const iterationAssociationsById = new Map();
      matchingProjectAllocations.forEach((allocation) => {
        const associatedIterationId = toValidNumberOrNull(allocation?.iteration_id);
        if (associatedIterationId == null) {
          return;
        }

        const knownIteration = iterationMetaById.get(associatedIterationId);
        const existingAssociation = iterationAssociationsById.get(associatedIterationId);
        iterationAssociationsById.set(associatedIterationId, {
          id: associatedIterationId,
          name:
            knownIteration?.name ||
            allocation?.iteration_name ||
            `Itération ${associatedIterationId}`,
          tint: knownIteration?.tint || getIterationTint(iterationAssociationsById.size),
          percentage:
            (existingAssociation?.percentage || 0) + (Number(allocation?.percentage) || 0),
        });
      });

      const directIterationId = toValidNumberOrNull(expense?.iteration_id);
      if (
        iterationAssociationsById.size === 0 &&
        directIterationId != null &&
        (projectIdNumeric == null || toValidNumberOrNull(expense?.project_id) === projectIdNumeric)
      ) {
        const knownIteration = iterationMetaById.get(directIterationId);
        iterationAssociationsById.set(directIterationId, {
          id: directIterationId,
          name:
            knownIteration?.name ||
            expense?.iteration_name ||
            `Itération ${directIterationId}`,
          tint: knownIteration?.tint || getIterationTint(0),
          percentage: 100,
        });
      }

      const iterationAssociations = Array.from(iterationAssociationsById.values());
      const selectedAssociation = iterationAssociations.find(
        (association) => association.id === iterationIdNumeric
      );
      const primaryIteration = selectedAssociation || iterationAssociations[0] || null;

      let projectPercentage = 0;
      if (matchingProjectAllocations.length > 0) {
        projectPercentage = matchingProjectAllocations.reduce(
          (sum, alloc) => sum + (Number(alloc?.percentage) || 0),
          0
        );
      } else if (
        projectIdNumeric != null &&
        !hasAllocations &&
        expense?.project_id != null &&
        toValidNumberOrNull(expense.project_id) === projectIdNumeric
      ) {
        projectPercentage = 100;
      }

      let iterationPercentage = 0;

      if (iterationIdNumeric != null) {
        const iterationAllocations = matchingProjectAllocations.filter((alloc) => {
          const normalizedIterationId = toValidNumberOrNull(alloc?.iteration_id);
          return normalizedIterationId != null && normalizedIterationId === iterationIdNumeric;
        });

        if (iterationAllocations.length > 0) {
          iterationPercentage = iterationAllocations.reduce(
            (sum, alloc) => sum + (Number(alloc?.percentage) || 0),
            0
          );
        } else if (
          projectIdNumeric != null &&
          !hasAllocations &&
          expense?.iteration_id != null &&
          toValidNumberOrNull(expense.iteration_id) === iterationIdNumeric &&
          toValidNumberOrNull(expense.project_id) === projectIdNumeric
        ) {
          iterationPercentage = 100;
        }
      }

      const computeAllocatedAmount = (percentage) => {
        if (amount == null || !Number.isFinite(amount) || percentage == null || percentage <= 0) {
          return null;
        }
        const raw = amount * (percentage / 100);
        return Math.round(raw * 100) / 100;
      };

      const projectAmount = computeAllocatedAmount(projectPercentage);
      const iterationAmount = computeAllocatedAmount(iterationPercentage);
      const projectAmountLabel = projectAmount != null ? formatCurrency(projectAmount, currency) : null;
      const iterationAmountLabel = iterationAmount != null ? formatCurrency(iterationAmount, currency) : null;
      const totalAmountLabel = amount != null ? formatCurrency(amount, currency) : null;
      const projectPercentageLabel = projectPercentage > 0 ? formatPercentage(projectPercentage) : null;
      const iterationPercentageLabel = iterationPercentage > 0 ? formatPercentage(iterationPercentage) : null;
      const showProjectShare = projectAmountLabel != null;
      const showIterationShare = iterationAmountLabel != null;

      return {
        ...expense,
        amount,
        currency,
        projectPercentage,
        projectAmount,
        iterationPercentage,
        iterationAmount,
        dateLabel: formatExpenseDate(expense?.date),
        fileName: getExpenseFilename(expense),
        projectAmountLabel,
        projectPercentageLabel,
        iterationAmountLabel,
        iterationPercentageLabel,
        totalAmountLabel,
        showProjectShare,
        showIterationShare,
        iterationAssociations,
        primaryIteration,
        displayedAmountLabel: showProjectShare
          ? projectAmountLabel
          : showIterationShare
          ? iterationAmountLabel
          : totalAmountLabel,
        displayedAmountTitle: showProjectShare
          ? 'Montant ventile sur le projet'
          : showIterationShare
          ? "Montant ventile sur l'iteration selectionnee"
          : 'Montant total de la depense',
      };
    });
  }, [expenses, iterations, projectId, selectedIterationId]);

  const filteredExpenses = useMemo(() => {
    const needle = normalizeSearch(searchTerm.trim());
    if (!needle) {
      return processedExpenses;
    }

    return processedExpenses.filter((expense) => {
      const haystack = normalizeSearch(
        [
          expense.id,
          expense.dateLabel,
          expense.displayedAmountLabel,
          expense.totalAmountLabel,
          expense.projectAmountLabel,
          expense.iterationAmountLabel,
          ...expense.iterationAssociations.map((association) => association.name),
          expense.description,
          expense.vendor,
          expense.employee_name,
          expense.created_by_user_name,
          expense.fileName,
        ]
          .filter(Boolean)
          .join(' ')
      );
      return haystack.includes(needle);
    });
  }, [processedExpenses, searchTerm]);

  const previewFileName = previewExpense?.fileName;
  const previewUrl = previewFileName ? getExpenseFileUrl(apiBaseUrl, previewFileName) : null;

  if (!processedExpenses.length) {
    return <p className="text-xs italic text-gray-500">Aucune depense pour ce projet.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative min-w-[220px] flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Rechercher une facture..."
            className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          {filteredExpenses.length} / {processedExpenses.length} factures
        </span>
      </div>

      {filteredExpenses.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
          Aucune facture ne correspond a la recherche.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 2xl:grid-cols-3">
          {filteredExpenses.map((expense) => {
            const canPreview = Boolean(expense.fileName);
            const totalAmountTextClass =
              expense.showProjectShare || expense.showIterationShare
                ? 'text-slate-500'
                : 'font-medium text-blue-700';
            const title = expense.vendor || expense.description || expense.fileName || `Depense #${expense.id}`;
            const iterationTint = expense.primaryIteration?.tint;

            return (
              <article
                key={expense.id}
                role={canPreview ? 'button' : undefined}
                tabIndex={canPreview ? 0 : undefined}
                onClick={() => canPreview && setPreviewExpense(expense)}
                onKeyDown={(event) => {
                  if (!canPreview || (event.key !== 'Enter' && event.key !== ' ')) {
                    return;
                  }
                  event.preventDefault();
                  setPreviewExpense(expense);
                }}
                className={`min-h-[148px] rounded-lg border p-3 transition ${
                  iterationTint
                    ? iterationTint.expenseCard
                    : canPreview
                    ? 'border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/40'
                    : 'border-slate-100 bg-slate-50/70'
                } ${
                  canPreview
                    ? 'cursor-pointer hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100'
                    : ''
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
                        {expense.dateLabel}
                      </span>
                      {canPreview && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                          <FileText className="h-3 w-3" />
                          PDF
                        </span>
                      )}
                    </div>
                    <h3 className="mt-1 truncate text-sm font-semibold text-slate-900" title={title}>
                      {title}
                    </h3>
                  </div>
                  {expense.displayedAmountLabel && (
                    <span
                      className={`shrink-0 rounded-md px-2 py-1 text-xs font-semibold ${
                        iterationTint?.expenseAmount || 'bg-slate-900 text-white'
                      }`}
                      title={expense.displayedAmountTitle}
                    >
                      {expense.displayedAmountLabel}
                    </span>
                  )}
                </div>

                {expense.description && expense.description !== title && (
                  <p className="mt-1 truncate text-xs text-slate-600" title={expense.description}>
                    {expense.description}
                  </p>
                )}

                {expense.iterationAssociations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Itérations associées">
                    {expense.iterationAssociations.map((association) => (
                      <span
                        key={association.id}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${association.tint.expenseBadge}`}
                        title={`Dépense associée à ${association.name}`}
                      >
                        {association.name}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {expense.showProjectShare && (
                    <span className="rounded-md bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-700">
                      Projet {expense.projectAmountLabel}
                      {expense.projectPercentageLabel && ` (${expense.projectPercentageLabel}%)`}
                    </span>
                  )}
                  {expense.showIterationShare && (
                    <span className="rounded-md bg-cyan-50 px-2 py-1 text-[11px] font-medium text-cyan-700">
                      Iteration {expense.iterationAmountLabel}
                      {expense.iterationPercentageLabel && ` (${expense.iterationPercentageLabel}%)`}
                    </span>
                  )}
                  {expense.totalAmountLabel && (
                    <span className={`rounded-md bg-slate-100 px-2 py-1 text-[11px] ${totalAmountTextClass}`}>
                      Total {expense.totalAmountLabel}
                    </span>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
                  <div className="flex min-w-0 items-center gap-3">
                    {expense.employee_name && (
                      <span
                        className="inline-flex min-w-0 items-center text-xs text-slate-500"
                        title={`Depense pour : ${expense.employee_name}`}
                      >
                        <User className="mr-1 h-3 w-3 shrink-0 text-slate-400" />
                        <span className="truncate">{expense.employee_name.split(' ')[0]}</span>
                      </span>
                    )}
                    {expense.created_by_user_name &&
                      (expense.created_by_user_id !== expense.employee_id || !expense.employee_name) && (
                        <span
                          className="inline-flex min-w-0 items-center text-xs text-blue-600"
                          title={`Ajoute par : ${expense.created_by_user_name}`}
                        >
                          <Edit3 className="mr-1 h-3 w-3 shrink-0 text-blue-400" />
                          <span className="truncate">{expense.created_by_user_name.split(' ')[0]}</span>
                        </span>
                      )}
                  </div>
                  <span className={`shrink-0 text-xs font-medium ${canPreview ? 'text-blue-600' : 'text-slate-400'}`}>
                    {canPreview ? 'Apercu' : 'Sans PDF'}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {previewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-3 sm:p-6"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewExpense(null)}
        >
          <div
            className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div className="min-w-0">
                <h3 className="truncate text-sm font-semibold text-slate-900">
                  {previewExpense.vendor || previewExpense.description || previewExpense.fileName}
                </h3>
                <p className="text-xs text-slate-500">
                  {previewExpense.dateLabel}
                  {previewExpense.displayedAmountLabel ? ` - ${previewExpense.displayedAmountLabel}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 px-2.5 text-xs font-medium text-slate-700 hover:border-blue-200 hover:text-blue-700"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Ouvrir
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewExpense(null)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-900"
                  aria-label="Fermer l'apercu"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 bg-slate-100">
              <iframe
                src={`${previewUrl}#toolbar=1&navpanes=0&view=FitH`}
                title={`Justificatif depense ${previewExpense.id}`}
                className="h-full w-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPanel;
